import classNames from 'classnames/bind';
import React from 'react';
import {
  Button, Col, Form, Row,
} from 'react-bootstrap';
import {
  MemoryDTO,
  MemoryLinkValue,
  MemoryValue, StepCheck,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { Character } from 'dorian-shared/types/character/Character';
import { MemoryBankCheckValue, MemorySlotCompareForm } from './MemorySlotCompareForm';
import styles from './Steps.scss';

const cs = classNames.bind(styles);

type Requirement = {
  check: StepCheck,
  visualTreatmentIfNotMet: string,
  isBadgeHidden: boolean,
}

export const VisualTreatmentIfNotMet: Record<string, string> = {
  Disable: 'Disable',
  Hide: 'Hide',
};

const getRequirementsVariableValues = (requirement?: Requirement) => {
  const requirementValues = {
    variableId: 0,
    operator: '',
    check: '' as MemoryValue | MemoryLinkValue,
    visualEffect: '',
  };

  if (requirement) {
    if (requirement.check) {
      requirementValues.variableId = requirement.check.variableId
        ? Number(requirement.check.variableId) : 0;
      requirementValues.operator = requirement.check.operator ? requirement.check.operator : '';
      // Exclude number[] (used by playerHas) - this component only handles memory slot checks
      const checkValue = requirement.check.value;
      requirementValues.check = (checkValue && !Array.isArray(checkValue)) ? checkValue : '';
    }
    requirementValues.visualEffect = requirement.visualTreatmentIfNotMet;
  }
  return requirementValues;
};

interface AnswerFormMemorySlotRequirementProps {
  requirement?: Requirement,
  isSlotRequirementActive: boolean,
  answerIndex: number,
  memoryBankSlots: MemoryDTO[],
  stepIndex: number,
  characters: Character[],
  onChangeCheckValue: (
    values: MemoryBankCheckValue[],
    stepIndex: number,
  ) => void,
  onChangeVisualEffectValue: (
    value: string,
  ) => void,
  onChangeBadgeVisibility: (
    value: boolean,
  ) => void,
  onDeleteRequirement: () => void,
  isPaidChoice?: boolean,
}

export function AnswerFormMemorySlotRequirement(props: AnswerFormMemorySlotRequirementProps) {
  const {
    requirement,
    isSlotRequirementActive,
    answerIndex,
    memoryBankSlots,
    stepIndex,
    characters,
    onChangeCheckValue,
    onChangeVisualEffectValue,
    onChangeBadgeVisibility,
    onDeleteRequirement,
    isPaidChoice,
  } = props;

  const {
    variableId,
    operator,
    check,
    visualEffect,
  } = getRequirementsVariableValues(requirement);

  const currentSlot = memoryBankSlots.find((slot) => slot.id === variableId);

  return (
    <Row className={cs('answerStepAdditionalDataContainer', isSlotRequirementActive ? 'd-block' : 'd-none')}>
      <Col
        className={cs('mx-0 pb-0 pt-2 border-top')}
      >
        <Row>
          <Col sm md>
            <MemorySlotCompareForm
              variableInputValue={variableId}
              operatorInputValue={operator}
              checkInputValue={check}
              currentSlot={currentSlot}
              inputNamePrefix={`steps[${stepIndex}][answers][${answerIndex}][requirement][check]`}
              memoryBankSlots={memoryBankSlots}
              stepIndex={stepIndex}
              characters={characters}
              onChangeValue={onChangeCheckValue}
            />
          </Col>
          <Col lg="auto">
            <Form.Label column>
              otherwise:
            </Form.Label>
          </Col>
          <Col sm={2} md={2}>
            <Form.Group
              as={Row}
            >
              <Form.Control
                size="sm"
                as="select"
                required
                name={`steps[${stepIndex}][answers][${answerIndex}][requirement][visualTreatmentIfNotMet]`}
                value={visualEffect}
                onChange={(event) => onChangeVisualEffectValue(event.target.value)}
              >
                <option value={VisualTreatmentIfNotMet.Disable} disabled={isPaidChoice}>
                  {VisualTreatmentIfNotMet.Disable}
                </option>
                <option value={VisualTreatmentIfNotMet.Hide}>{VisualTreatmentIfNotMet.Hide}</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col lg="auto" className="mt-1 ml-3">
            <Form.Control
              type="hidden"
              name={`steps[${stepIndex}][answers][${answerIndex}][requirement][isBadgeHidden]`}
              value={isPaidChoice || requirement?.isBadgeHidden === true ? 'true' : 'false'}
            />
            <Form.Check
              custom
              id={`steps[${stepIndex}][answers][${answerIndex}][requirement][isBadgeHidden]`}
              checked={isPaidChoice || requirement?.isBadgeHidden === true}
              onChange={(event) => onChangeBadgeVisibility(event.target.checked)}
              disabled={isPaidChoice}
              type="checkbox"
              label="Hide indicator"
            />
          </Col>
          <Col sm={2} xl={1} className="text-right">
            <Button
              className="ml-1 btnDeleteImgSm"
              size="sm"
              variant="secondary"
              onClick={() => onDeleteRequirement()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
